import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import SalesPopups from "@/pages/SalesPopups";

// Render a small subset of Claude's markdown (bold + line breaks) inside the
// chat bubbles. Plain text otherwise — no arbitrary HTML.
function renderChatMarkdown(text) {
  if (!text) return null;
  // Escape any raw HTML in the source, then re-introduce our safe tags.
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const html = escaped
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(?!\s)([^*\n]+?)(?!\s)\*/g, "<em>$1</em>")
    .replace(/\n{2,}/g, "<br/><br/>")
    .replace(/\n/g, "<br/>");
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

/**
 * HeroChat — a slick, always-visible chat card that lives in the hero.
 *
 * - Uses the tenant's existing /api/public/chat endpoint (Claude Sonnet 4.5).
 * - Persists the session id per browser so the conversation carries across
 *   route changes.
 * - Personality is set on the backend via `ai_settings.system_prompt` — this
 *   component is purely presentational.
 */
export default function HeroChat({ tenant, isSpanish = false }) {
  const scrollerRef = useRef(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => {
    const key = `nariai_hero_chat_${tenant?.slug || "default"}`;
    let sid = localStorage.getItem(key);
    if (!sid) {
      sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem(key, sid);
    }
    return sid;
  });

  const openingBot = isSpanish
    ? "Bienvenido a Lone Star Striping. Mi objetivo hoy no es venderle — es ayudarle a tomar la mejor decisión para su propiedad. Cuénteme un poco sobre su lote y le acompaño paso a paso."
    : "Welcome to Lone Star Striping. My goal today isn't to sell you — it's to help you make the best decision for your property. Tell me a little about your lot and I'll walk you through it, step by step.";

  const chips = isSpanish
    ? [
        "Administramos un centro comercial — ¿por dónde empezamos?",
        "¿Cuál es su proceso de la primera llamada al lote terminado?",
        "¿Cómo manejan el cumplimiento ADA?",
        "¿Qué tan pronto pueden agendarnos?",
      ]
    : [
        "We manage a shopping center — where do we start?",
        "What's your process from first call to finished lot?",
        "How do you handle ADA compliance?",
        "How soon can you get us on the schedule?",
      ];

  const [messages, setMessages] = useState([{ from: "ai", text: openingBot }]);
  const [visitorName, setVisitorName] = useState(() => {
    try {
      return localStorage.getItem(`nariai_hero_chat_name_${tenant?.slug || "default"}`) || "";
    } catch {
      return "";
    }
  });
  const [popupTrigger, setPopupTrigger] = useState(null);

  // Popup 1: welcome — fires shortly after mount, once
  useEffect(() => {
    const id = setTimeout(() => setPopupTrigger("welcome"), 1200);
    return () => clearTimeout(id);
  }, []);

  // Track user-message count for popup 2 (process) and popup 3 (next)
  const userMsgCount = messages.filter((m) => m.from === "user").length;
  useEffect(() => {
    if (userMsgCount === 2) setPopupTrigger("process");
    if (userMsgCount === 3) setPopupTrigger("next");
  }, [userMsgCount]);

  // Persist visitor name
  useEffect(() => {
    try {
      if (visitorName) {
        localStorage.setItem(`nariai_hero_chat_name_${tenant?.slug || "default"}`, visitorName);
      }
    } catch {
      /* ignore */
    }
  }, [visitorName, tenant?.slug]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, busy]);

  async function send(text) {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { from: "user", text: msg }]);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/chat`, {
        tenant_slug: tenant.slug,
        session_id: sessionId,
        message: msg,
        visitor_name: visitorName || undefined,
      });
      const rawReply = data?.reply || (isSpanish
        ? "Perdón — se me trabó la señal un momento. Llame a Patrick al (469) 626-2466."
        : "Sorry — my signal glitched for a second. Just call Patrick at (469) 626-2466.");
      // Extract the private [SIGNAL:BUYING_INTENT] marker (never shown to visitor).
      const hasBuyingIntent = /\[SIGNAL:BUYING_INTENT\]/i.test(rawReply);
      const reply = rawReply.replace(/\[SIGNAL:[A-Z_]+\]/gi, "").trim();
      setMessages((m) => [...m, { from: "ai", text: reply }]);
      // Popup 4: buying-signal celebration — Popup 5 follows 8s later
      if (hasBuyingIntent) {
        setPopupTrigger("buying");
        setTimeout(() => setPopupTrigger("scheduled"), 8000);
      }
      // Fire GA4 event
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "hero_chat_message", { tenant_slug: tenant.slug });
        if (hasBuyingIntent) {
          window.gtag("event", "hero_chat_buying_intent", { tenant_slug: tenant.slug });
        }
      }
    } catch {
      setMessages((m) => [
        ...m,
        {
          from: "ai",
          text: isSpanish
            ? "Ups, un pequeño problema técnico. Llame a Patrick directo al (469) 626-2466."
            : "Whoa, quick technical hiccup. Give Patrick a direct call at (469) 626-2466.",
        },
      ]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="hero-chat-card"
      data-testid="hero-chat-card"
      style={{
        position: "relative",
        width: "100%",
        maxWidth: 420,
        borderRadius: 6,
        overflow: "hidden",
        background: "linear-gradient(180deg, rgba(15,15,15,0.92) 0%, rgba(24,20,10,0.95) 100%)",
        border: "1px solid rgba(212,175,55,0.35)",
        boxShadow: "0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(212,175,55,0.08)",
        backdropFilter: "blur(6px)",
      }}
    >
      <SalesPopups trigger={popupTrigger} visitorName={visitorName} isSpanish={isSpanish} />
      {/* Gold hairline at top */}
      <div
        aria-hidden
        style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 1,
          background: "linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.7) 50%, transparent 100%)",
        }}
      />

      {/* Header — "Talk to us now" */}
      <div
        style={{
          padding: "16px 18px",
          borderBottom: "1px solid rgba(212,175,55,0.16)",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div style={{ position: "relative", width: 40, height: 40, flexShrink: 0 }}>
          <div
            style={{
              width: "100%", height: "100%", borderRadius: 999,
              background: "linear-gradient(180deg,#F0D580 0%,#C9A24B 50%,#8F6A2C 100%)",
              boxShadow: "0 2px 12px rgba(201,162,75,0.45), inset 0 1px 0 rgba(255,255,255,0.5)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#0b0b0b", fontWeight: 900, fontSize: 15, fontFamily: "'Cabinet Grotesk', 'Inter', sans-serif",
              letterSpacing: "-0.005em",
            }}
          >
            S
          </div>
          {/* pulse online-dot */}
          <span
            aria-hidden
            style={{
              position: "absolute", bottom: 0, right: 0,
              width: 10, height: 10, borderRadius: 999,
              background: "#5BC46B",
              boxShadow: "0 0 0 2px #0b0b0b, 0 0 10px rgba(91,196,107,0.9)",
              animation: "hero-chat-pulse 1.6s ease-in-out infinite",
            }}
          />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontFamily: "'Cabinet Grotesk', 'Inter', sans-serif",
              fontWeight: 800, color: "#FFFFFF", fontSize: 15, letterSpacing: "-0.005em", lineHeight: 1.15,
            }}
          >
            {isSpanish ? "Hable con nosotros ahora" : "Talk to us now"}
          </div>
          <div
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, marginTop: 2,
              fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
              color: "#5BC46B",
            }}
          >
            <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: "#5BC46B", boxShadow: "0 0 8px #5BC46B" }} />
            {isSpanish ? "Stripe · En línea" : "Stripe · Live now"}
          </div>
        </div>
      </div>

      {/* Message scroller */}
      <div
        ref={scrollerRef}
        style={{
          height: 260,
          overflowY: "auto",
          padding: "16px 16px 8px 16px",
          background: "linear-gradient(180deg, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0) 100%)",
        }}
        className="hero-chat-scroller"
        data-testid="hero-chat-scroller"
      >
        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              justifyContent: m.from === "user" ? "flex-end" : "flex-start",
              marginBottom: 10,
            }}
          >
            <div
              style={{
                maxWidth: "85%",
                padding: "9px 13px",
                borderRadius: m.from === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: 13.5,
                lineHeight: 1.5,
                background: m.from === "user"
                  ? "linear-gradient(180deg,#F0D580 0%,#C9A24B 100%)"
                  : "rgba(255,255,255,0.05)",
                color: m.from === "user" ? "#0b0b0b" : "rgba(245,245,240,0.95)",
                border: m.from === "user" ? "none" : "1px solid rgba(212,175,55,0.12)",
                boxShadow: m.from === "user" ? "0 4px 12px rgba(201,162,75,0.3)" : "none",
                whiteSpace: "pre-wrap",
              }}
            >
              {m.from === "ai" ? renderChatMarkdown(m.text) : m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 10 }}>
            <div
              style={{
                padding: "10px 14px",
                borderRadius: "14px 14px 14px 4px",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(212,175,55,0.12)",
                color: "rgba(245,245,240,0.6)",
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: 13,
                display: "inline-flex",
                gap: 4,
                alignItems: "center",
              }}
            >
              <span className="hero-chat-dot" />
              <span className="hero-chat-dot" style={{ animationDelay: "0.15s" }} />
              <span className="hero-chat-dot" style={{ animationDelay: "0.3s" }} />
            </div>
          </div>
        )}
      </div>

      {/* Suggested chips — only show if this is a fresh conversation */}
      {messages.length <= 1 && (
        <div
          style={{
            padding: "0 12px 8px 12px",
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
          }}
        >
          {chips.map((c, i) => (
            <button
              key={i}
              type="button"
              onClick={() => send(c)}
              disabled={busy}
              data-testid={`hero-chat-chip-${i + 1}`}
              style={{
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: 11.5,
                fontWeight: 500,
                letterSpacing: "0.005em",
                padding: "6px 10px",
                borderRadius: 999,
                background: "rgba(212,175,55,0.08)",
                border: "1px solid rgba(212,175,55,0.32)",
                color: "rgba(245,245,240,0.85)",
                cursor: "pointer",
                transition: "background 200ms ease, color 200ms ease, border-color 200ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(212,175,55,0.18)";
                e.currentTarget.style.color = "#F0D580";
                e.currentTarget.style.borderColor = "rgba(212,175,55,0.55)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(212,175,55,0.08)";
                e.currentTarget.style.color = "rgba(245,245,240,0.85)";
                e.currentTarget.style.borderColor = "rgba(212,175,55,0.32)";
              }}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {/* Optional name capture — makes closing popup personal */}
      {!visitorName && (
        <div style={{ padding: "0 12px 8px 12px" }}>
          <input
            type="text"
            placeholder={isSpanish ? "Su nombre (opcional)" : "Your name (optional)"}
            data-testid="hero-chat-name-input"
            defaultValue=""
            onBlur={(e) => {
              const v = e.target.value.trim().slice(0, 60);
              if (v) setVisitorName(v);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                const v = e.currentTarget.value.trim().slice(0, 60);
                if (v) setVisitorName(v);
              }
            }}
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: 999,
              border: "1px solid rgba(212,175,55,0.18)",
              background: "rgba(0,0,0,0.35)",
              color: "rgba(245,245,240,0.9)",
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: 12,
              outline: "none",
              transition: "border-color 200ms ease",
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = "rgba(212,175,55,0.55)"; }}
          />
        </div>
      )}

      {/* Composer */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        style={{ display: "flex", gap: 6, padding: 12, borderTop: "1px solid rgba(212,175,55,0.14)" }}
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={isSpanish ? "Escriba su pregunta…" : "Ask about pricing, timing, ADA…"}
          disabled={busy}
          data-testid="hero-chat-input"
          style={{
            flex: 1,
            minWidth: 0,
            padding: "10px 12px",
            borderRadius: 999,
            border: "1px solid rgba(212,175,55,0.28)",
            background: "rgba(0,0,0,0.4)",
            color: "#FFFFFF",
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: 13,
            outline: "none",
            transition: "border-color 200ms ease, box-shadow 200ms ease",
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = "rgba(212,175,55,0.65)"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(212,175,55,0.15)"; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(212,175,55,0.28)"; e.currentTarget.style.boxShadow = "none"; }}
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          data-testid="hero-chat-send"
          style={{
            padding: "0 16px",
            borderRadius: 999,
            border: "none",
            background: "linear-gradient(180deg,#F0D580 0%,#C9A24B 50%,#8F6A2C 100%)",
            color: "#0b0b0b",
            fontFamily: "'Cabinet Grotesk', 'Inter', sans-serif",
            fontWeight: 800,
            fontSize: 12,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            cursor: busy || !input.trim() ? "not-allowed" : "pointer",
            opacity: busy || !input.trim() ? 0.55 : 1,
            transition: "transform 150ms ease, box-shadow 150ms ease",
            boxShadow: "0 6px 16px rgba(201,162,75,0.3), inset 0 1px 0 rgba(255,255,255,0.4)",
          }}
          onMouseEnter={(e) => { if (!e.currentTarget.disabled) { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(201,162,75,0.45), inset 0 1px 0 rgba(255,255,255,0.4)"; } }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(201,162,75,0.3), inset 0 1px 0 rgba(255,255,255,0.4)"; }}
        >
          {isSpanish ? "Enviar" : "Send"}
        </button>
      </form>

      <div
        style={{
          padding: "0 12px 12px 12px",
          textAlign: "center",
          fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
          fontSize: 9.5,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "rgba(245,245,240,0.4)",
        }}
      >
        {isSpanish
          ? "Respuestas al instante · Sin formularios · Sin ventas agresivas"
          : "Instant answers · No forms · No hard sell"}
      </div>

      <style>{`
        @keyframes hero-chat-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.25); opacity: 0.7; }
        }
        .hero-chat-scroller::-webkit-scrollbar { width: 6px; }
        .hero-chat-scroller::-webkit-scrollbar-track { background: transparent; }
        .hero-chat-scroller::-webkit-scrollbar-thumb {
          background: rgba(212,175,55,0.2); border-radius: 999px;
        }
        .hero-chat-scroller::-webkit-scrollbar-thumb:hover {
          background: rgba(212,175,55,0.4);
        }
        .hero-chat-dot {
          display: inline-block;
          width: 5px; height: 5px;
          border-radius: 999px;
          background: rgba(212,175,55,0.6);
          animation: hero-chat-typing 1s ease-in-out infinite;
        }
        @keyframes hero-chat-typing {
          0%, 100% { transform: translateY(0); opacity: 0.4; }
          50% { transform: translateY(-3px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
